import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import React, {
  FunctionComponent,
  useCallback,
  useState,
  type JSX,
} from 'react';
import Checkbox from '../checkbox/Checkbox';
import FieldLabel, { LabelStyleVariant } from '../fieldLabel/FieldLabel';
import InputWrapper, {
  InputColourVariant,
  InputStyleVariant,
  InputWidth,
} from '../inputWrapper/InputWrapper';
import './DropdownField.sass';

type Props = {
  className?: string;
  styleVariant?: InputStyleVariant;
  inputWidth?: InputWidth;
  labelStyleVariant?: LabelStyleVariant;
  colourVariant?: InputColourVariant;
  name: string;
  value?: any;
  label?: string;
  description?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  iconBefore?: TIconName | JSX.Element;
  iconAfter?: TIconName;
  optionGroups?: { name?: string; id: number }[];
  options: {
    value: any;
    label: string;
    isDisabled?: boolean;
    optionGroupId?: number;
  }[];
  multiple?: boolean;
  additionalSelectProps?: { [key: string]: any };
  onChange?: (value: { [key: string]: any }) => void;
  onTouch?: (fieldName: string) => void;
  hasBoxShadow?: boolean;
  displayCheckboxes?: boolean;
  emptyText?: string;
  minToShorten?: number; // set for list of labels to be x, y +2 more
  renderOption?: (option: {
    value: any;
    label: string;
    optionGroupId?: number;
    className?: string;
  }) => React.ReactNode;
  renderValue?: (value: any) => React.ReactNode;
  onBlur?: () => void;
};

const DropdownField: FunctionComponent<React.PropsWithChildren<Props>> = ({
  className,
  styleVariant,
  inputWidth,
  labelStyleVariant,
  colourVariant,
  name,
  value,
  label,
  description,
  isDisabled = false,
  isRequired = false,
  iconBefore,
  iconAfter,
  options,
  optionGroups,
  multiple = false,
  additionalSelectProps = {},
  onChange,
  onTouch,
  hasBoxShadow = true,
  displayCheckboxes = false,
  emptyText,
  minToShorten,
  renderOption,
  renderValue,
  onBlur,
}: React.PropsWithChildren<Props>): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  const onInputChange = useCallback(
    (event: SelectChangeEvent<any>) => {
      onChange?.({
        [event.target.name]: event.target.value,
      });
    },
    [onChange]
  );

  const renderOptions = useCallback(
    (
      options: {
        value: any;
        label: string;
        isDisabled?: boolean;
        optionGroupId?: number;
        tagColour?: string;
        className?: string;
      }[]
    ) => {
      return options.map((option, index: number) => {
        return (
          <MenuItem
            value={option.value ?? undefined}
            key={`option-${index}`}
            className={option.className}
            disabled={option.isDisabled}
          >
            {displayCheckboxes && (
              <Checkbox
                isChecked={
                  Array.isArray(value)
                    ? value.includes(option.value)
                    : value === option.value ||
                      (value === null && option.value === undefined)
                }
                onChange={() => null}
                isDisabled={option.isDisabled}
                className="!py-0 !pl-0"
              />
            )}
            {renderOption ? renderOption(option) : <span>{option.label}</span>}
          </MenuItem>
        );
      });
    },
    [displayCheckboxes, value, renderOption]
  );

  return (
    <div className={`dropdown-field ${className ? className : ''}`}>
      <FieldLabel
        label={label}
        isRequired={isRequired}
        description={description}
        styleVariant={labelStyleVariant}
      />
      <InputWrapper
        isDisabled={isDisabled}
        styleVariant={styleVariant}
        colourVariant={colourVariant}
        inputWidth={inputWidth}
        hasBoxShadow={hasBoxShadow}
      >
        <>
          {iconBefore && (
            <span
              className="input-wrapper__icon-wrapper"
              onClick={() => setIsOpen(true)}
            >
              {React.isValidElement(iconBefore) ? (
                iconBefore
              ) : (
                <UntitledIcon
                  className={'input-wrapper__pre-icon'}
                  name={iconBefore as TIconName}
                />
              )}
            </span>
          )}
          <Select
            onBlur={onBlur}
            value={value === undefined ? '' : value}
            name={name}
            onChange={onInputChange}
            displayEmpty={true}
            disableUnderline
            disabled={isDisabled}
            onClose={() => {
              setIsOpen(false);
              onTouch?.(name);
            }}
            variant="standard"
            renderValue={(value: any) => {
              const selectedOptions = options.filter((x) => {
                return Array.isArray(value)
                  ? value.includes(x.value)
                  : value === x.value ||
                      (value === null && x.value === undefined);
              });

              const numberOfSelectedOptions = selectedOptions?.length;
              const numberOfShownOptions =
                minToShorten && numberOfSelectedOptions > minToShorten
                  ? minToShorten
                  : numberOfSelectedOptions;
              const numberOfHiddenOptions =
                numberOfSelectedOptions - numberOfShownOptions;

              return (
                <span className="dropdown-field__selected-label">
                  {renderValue ? (
                    renderValue(value)
                  ) : (
                    <>
                      {!numberOfSelectedOptions && emptyText}
                      {selectedOptions
                        .slice(0, numberOfShownOptions)
                        .map((x) => x.label)
                        .join(', ')}
                      {!!numberOfHiddenOptions && (
                        <span className="dropdown-more-values-amount">
                          {' '}
                          +{numberOfHiddenOptions} more
                        </span>
                      )}
                    </>
                  )}
                </span>
              );
            }}
            onOpen={() => setIsOpen(true)}
            open={isOpen}
            multiple={multiple}
            IconComponent={(props: any) => {
              return (
                <div
                  className={`dropdown-field__icon-component ${props.className}`}
                >
                  <UntitledIcon name="chevron-down" />
                </div>
              );
            }}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'center',
              },
              classes: { paper: 'dropdown-field__paper' },
            }}
            {...additionalSelectProps}
          >
            {renderOptions(options.filter((option) => !option.optionGroupId))}
            {optionGroups &&
              optionGroups.map((optionGroup, index: number) => {
                return [
                  <div
                    className="dropdown-field__option-group"
                    key={`option-group-${index}`}
                  >
                    <hr />
                    {optionGroup.name && (
                      <small className="dropdown-field__option-group-name">
                        {optionGroup.name}
                      </small>
                    )}
                  </div>,
                  ...renderOptions(
                    options.filter(
                      (option) => option.optionGroupId === optionGroup.id
                    )
                  ),
                ];
              })}
          </Select>
          {iconAfter && (
            <span
              className="input-wrapper__icon-wrapper"
              onClick={() => setIsOpen(true)}
            >
              <UntitledIcon
                className={'input-wrapper__post-icon'}
                name={iconAfter}
              />
            </span>
          )}
        </>
      </InputWrapper>
    </div>
  );
};

export default DropdownField;
